import {
  ESG_CAT1_OR_2_HELPER,
  ESG_CATEGORY_LABELS,
  ESG_DISPOSAL_ADDER_BASIS_NOTE,
} from '@siliconx/shared';

/**
 * SIL-475 — v0 Scope 3 upstream/downstream section copy. Authority:
 * resources/plans/sil-463-scope3-category-matrix.md (rows, groupings,
 * basis, and the exclusion-justification strings below are copied from it
 * verbatim, never paraphrased).
 *
 * Cat 1/2 and Cat 4's row labels + the Cat 1/2 helper are the SIL-426 §4
 * locked strings, reused verbatim via `ESG_CATEGORY_LABELS` /
 * `ESG_CAT1_OR_2_HELPER` — never retyped. That inventory covers the
 * scenario-forecast tool only, which deliberately excludes Cat 11/12 (its
 * own §6 sell-action note), so this file supplies the Cat 11/12 labels and
 * every exclusion-justification string fresh — under the SAME copy
 * discipline the locked inventory itself documents: "consistent with",
 * never "per"; never the words offset / credit / carbon neutral, and never
 * "capital goods" as a label.
 */
export const SCOPE3_CAT1_2_LABEL = ESG_CATEGORY_LABELS.cat1_or_2;
export const SCOPE3_CAT4_LABEL = ESG_CATEGORY_LABELS.cat4;
export const SCOPE3_CAT11_LABEL = 'Category 11 · use of sold products';
export const SCOPE3_CAT12_LABEL = 'Category 12 · end-of-life of sold products';

/**
 * SIL-488 — Category 8/9 labels + method notes, for the Variant B matrix
 * (`Scope3CategoryMatrix.tsx`). Both categories are IN per the matrix's
 * summary verdict table but have no serving surface yet (no
 * `useScope3Cat8`/`useScope3Cat9` hook exists) — the matrix renders them as
 * applicability badges, not values, until that data lands. Copied from the
 * SIL-464 prototype's Variant B stub verbatim.
 */
export const SCOPE3_CAT8_LABEL = 'Category 8 · upstream leased assets (colocation electricity)';
export const SCOPE3_CAT8_NOTE =
  'Colocation electricity is assigned to Category 8 by platform convention; depending on your lease terms and consolidation approach it may belong in Scope 2. Disclosed in the methodology sheet.';
export const SCOPE3_CAT9_LABEL = 'Category 9 · downstream transportation';
export const SCOPE3_CAT9_NOTE =
  "Shipping of sold hardware paid by the buyer. Freight your organization pays is reported under Category 4 instead (Scope 3 Standard, Table 5.4 who-pays rule).";

/** Cat 1/2 row helper — SIL-426 §4, verbatim. */
export const SCOPE3_CAT1_2_NOTE = ESG_CAT1_OR_2_HELPER;

/**
 * Cat 11 row helper — Box 11.2 mitigation (S3 p115): longer assumed
 * lifetimes raise the reported figure, and a rate measured under the
 * seller's own grid does not transfer to whichever grid the buyer runs the
 * hardware on. Composed for this new surface (no locked sentence exists —
 * the forecast tool's copy inventory excludes Cat 11 entirely), grounded in
 * the matrix's Category 11 section and following its same verb discipline.
 */
export const SCOPE3_CAT11_NOTE =
  "Estimated from the annual use-phase rate at transfer x a default remaining lifetime. A rate measured under your organization's grid does not transfer to whichever grid the buyer runs the hardware on.";

/**
 * Cat 12 row helper — the SIL-426 §9 disposal-adder-basis string, reused
 * verbatim (the matrix's Category 12 section names this exact string as the
 * boundary note to state on the row).
 */
export const SCOPE3_CAT12_NOTE = ESG_DISPOSAL_ADDER_BASIS_NOTE;

export interface Scope3Exclusion {
  category: string;
  justification: string;
}

/**
 * Every OUT-verdict category from the matrix's summary table, with its
 * locked-style justification string (matrix "Exclusion-justification
 * strings" section + Category 13's own deep-row string) — copied
 * byte-for-byte. Cat 8 and Cat 9 are IN in the full matrix but simply not
 * built yet for v0 (deferred to the post-SIL-464 prototype per the matrix's
 * "v0 demo rows" section) — they are NOT listed here, since this footnote
 * is the S3 ch11 required-reporting disclosure for categories judged
 * genuinely inapplicable or not estimable, not a scope-cut TODO list.
 */
export const SCOPE3_EXCLUSIONS: Scope3Exclusion[] = [
  {
    category: '3',
    justification:
      "Category 3 (fuel- and energy-related activities) is excluded. Electricity figures on this dashboard use combustion-stage grid factors; upstream fuel extraction, production, and transmission and distribution losses are not estimable from the data the platform holds. This exclusion and its rationale are disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '5',
    justification:
      "Category 5 (waste generated in operations) is not shown in this section. Disposal of retired hardware is reported in your footprint under 'Category 5 · end-of-life disposal', covering collection and transport to the recycling facility only, consistent with the GHG Protocol recycling guidance (Scope 3 Standard, Box 5.6). Waste unrelated to platform-managed hardware is not modeled.",
  },
  {
    category: '6',
    justification:
      "Category 6 (business travel) is excluded. Travel is unrelated to platform-managed hardware and is not estimable from the data the platform holds. Disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '7',
    justification:
      "Category 7 (employee commuting) is excluded. Commuting is unrelated to platform-managed hardware and is not estimable from the data the platform holds. Disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '10',
    justification:
      "Category 10 (processing of sold products) is excluded. Hardware sold via the platform is a final product placed into service, not an intermediate product processed by downstream manufacturers, so the category does not apply. Disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '13',
    justification:
      "Category 13 (downstream leased assets) is excluded. The platform records ownership transfers, not lease agreements, so emissions from hardware your organization leases to others cannot be estimated from platform data. If you lease out equipment, those use-phase emissions belong in Category 13 of your own inventory. This exclusion and its rationale are disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '14',
    justification:
      "Category 14 (franchises) is excluded. Franchise operations are outside the platform's data boundary; if your organization operates franchises, account for them in your own inventory. Disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
  {
    category: '15',
    justification:
      "Category 15 (investments) is excluded. Emissions from equity, debt, and project-finance holdings are outside the platform's data boundary. Disclosed consistent with the GHG Protocol Scope 3 Standard's practice of listing excluded categories with justification.",
  },
];

const exclusionByCategory = new Map(SCOPE3_EXCLUSIONS.map((e) => [e.category, e.justification]));

/** 'yes' = applies; 'depends' = conditional on the org's own facts (lease
 * terms, capitalization policy, colocation vs owned facility — never
 * resolved by the platform); 'no' = does not apply / excluded. */
export type Scope3Applicability = 'yes' | 'depends' | 'no';

export interface Scope3MatrixRow {
  /** '1/2' for the merged row, otherwise the bare category number. */
  category: string;
  shortName: string;
  seller: Scope3Applicability;
  buyerNew: Scope3Applicability;
  buyerUsed: Scope3Applicability;
  /** True for every OUT-verdict row from the matrix's summary table — the
   * row renders dimmed and its method note is the exclusion justification. */
  excluded: boolean;
  /** Method note shown in the row's hover/expand affordance — the
   * exclusion justification for excluded rows, or a computation/convention
   * note for IN rows (mirrors the deep-row sections of the matrix doc). */
  note: string;
}

/**
 * SIL-488 — Variant B category x persona matrix (all 15 GHG Protocol Scope
 * 3 categories, 1/2 merged into one row per SIL-426 §4 lock). Source of
 * truth: `resources/plans/sil-463-scope3-category-matrix.md`'s "Summary
 * verdict table" (P-S/P-BN/P-BX columns) + "One-line n/a rows" section.
 * Never hand-derive applicability elsewhere — extend this list instead.
 */
export const SCOPE3_MATRIX_ROWS: Scope3MatrixRow[] = [
  {
    category: '1/2',
    shortName: SCOPE3_CAT1_2_LABEL,
    seller: 'yes',
    buyerNew: 'yes',
    buyerUsed: 'yes',
    excluded: false,
    note: SCOPE3_CAT1_2_NOTE,
  },
  {
    category: '3',
    shortName: 'Fuel- and energy-related activities',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('3')!,
  },
  {
    category: '4',
    shortName: SCOPE3_CAT4_LABEL,
    seller: 'yes',
    buyerNew: 'yes',
    buyerUsed: 'yes',
    excluded: false,
    note:
      'Weight × transport distance × freight factor, per asset class. Basis and formula echoed on the value.',
  },
  {
    category: '5',
    shortName: 'Waste generated in operations',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('5')!,
  },
  {
    category: '6',
    shortName: 'Business travel',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('6')!,
  },
  {
    category: '7',
    shortName: 'Employee commuting',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('7')!,
  },
  {
    category: '8',
    shortName: SCOPE3_CAT8_LABEL,
    seller: 'depends',
    buyerNew: 'depends',
    buyerUsed: 'depends',
    excluded: false,
    note: SCOPE3_CAT8_NOTE,
  },
  {
    category: '9',
    shortName: SCOPE3_CAT9_LABEL,
    seller: 'yes',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: false,
    note: SCOPE3_CAT9_NOTE,
  },
  {
    category: '10',
    shortName: 'Processing of sold products',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('10')!,
  },
  {
    category: '11',
    shortName: SCOPE3_CAT11_LABEL,
    seller: 'yes',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: false,
    note: SCOPE3_CAT11_NOTE,
  },
  {
    category: '12',
    shortName: SCOPE3_CAT12_LABEL,
    seller: 'yes',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: false,
    note: SCOPE3_CAT12_NOTE,
  },
  {
    category: '13',
    shortName: 'Downstream leased assets',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('13')!,
  },
  {
    category: '14',
    shortName: 'Franchises',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('14')!,
  },
  {
    category: '15',
    shortName: 'Investments',
    seller: 'no',
    buyerNew: 'no',
    buyerUsed: 'no',
    excluded: true,
    note: exclusionByCategory.get('15')!,
  },
];
